import { useEffect, useState } from "react";
import { Alert, Card, Col, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Column } from "react-table";
import { Grid } from "../../leap-components/Grid";
import LeapSearch from "./Search";
import { CreateStaticSelectColumnFilter } from "../../leap-components/Grid/FetchGrid/StaticSelectColumnFilter";
import { EpaGridOrderByFetchParams } from "../../leap-components/Grid/EpaGridProps";
import {
  IAuthorisationTypesList,
  ILicenceSearchList,
  GetLicenceAdvancedSearchFetchUrl,
  ILicenceSearchRow,
  ISectorsList,
  ICountyList,
  ILicenceStatusesList,
  YesNoAllFilterValues,
} from "../../api/ILicenceAdvancedSearch";
import { formatDate } from "../../api/DateUtils";
import { GetLicenceVersionUrl } from "../../external-apps/Config";
import { InformationIcon } from "../../leap-components/icons/LeapIcons";
import { MediaObj } from "../../hooks/useDetectScreenBreakpoints";

interface ISearchResultRow {
  licence_name: string;
  profile_number: string;
  organisation: string;
  active_application: string;
  county: string;
}

interface IAdvancedSearchProps {
  authorisationTypes: IAuthorisationTypesList;
  sectorsList: ISectorsList;
  statusesList: ILicenceStatusesList;
  countiesList: ICountyList;
  screenBreakpoints: MediaObj;
}

const GetLicenceVersionUrlFromRow = (row: any) => {
  return GetLicenceVersionUrl(
    row.original.authorisationtype,
    row.original.authorisationnumber.split("-")[0],
    row.original.licenceid,
    row.original.authorisationnumber
  );
};

const alertColor = "secondary";

const LeapAlert = () => (
  <Alert variant={alertColor} dismissible>
    <div className="alert-icon">
      <InformationIcon />
    </div>
    <div className="alert-message">
      <h4>
        <b>LEAP Search Function Overview</b>
      </h4>
      <h5>
        <b>Using the Search Bar</b>
      </h5>
      <p>
        The search bar allows you to quickly find information when you know a
        specific detail, such as applicant or licencee name, number or other
        identifying information. Type your search term into the bar and submit
        the search to view results.
      </p>
      <h5>
        <b>Using Filters to Search</b>
      </h5>
      <p>
        Filters help you narrow down results when you do not have a specific
        search detail. You can refine your search using options such as
        authorisation type, status, sector, and county. Filters can be used
        individually or together to better match what you are looking for. After
        completing a filtered search, select Clear All Filters before starting a
        new search to ensure accurate results.
      </p>
    </div>
  </Alert>
);

const AdvancedSearch = ({
  authorisationTypes,
  sectorsList,
  statusesList,
  countiesList,
  screenBreakpoints,
}: IAdvancedSearchProps) => {
  const [searchValue, setSearchValue] = useState("");

  const searchChangedFn = (newSearch: string) => {
    setSearchValue(newSearch);
  };

  const tableColumnsSmall: Column<Record<string, ILicenceSearchRow>>[] = [
    {
      Header: "Number",
      accessor: "authorisationnumber",
      Filter: false,
      filter: "authorisationnumber",
      Cell: (rowInfo: any) => (
        <table>
          <tr>
            <td>
              <a
                href={GetLicenceVersionUrlFromRow(rowInfo.row)}
                target="_blank"
              >
                {rowInfo.value}
              </a>
            </td>
          </tr>
        </table>
      ),
      sortType: "basic",
    },
    {
      Header: "Name",
      accessor: "authorisationname",
      Filter: false,
      sortType: "basic",
    },
    {
      Header: "Authorisation Type",
      accessor: "authorisationtype",
      Filter: CreateStaticSelectColumnFilter(
        authorisationTypes.list.map((authType) => authType.value)
      ),
    },
    {
      Header: "Organisation",
      accessor: "organisation",
      Filter: false,
    },
    {
      Header: "County",
      accessor: "county",
      Filter: CreateStaticSelectColumnFilter(
        countiesList.list.map((county) => county.value)
      ),
    },
    {
      Header: "Status",
      accessor: "status",
      Filter: CreateStaticSelectColumnFilter(
        statusesList.list.map((status) => status.value)
      ),
    },
  ];

  const tableColumnsMedium: Column<Record<string, ILicenceSearchRow>>[] = [
    {
      Header: "Number",
      accessor: "authorisationnumber",
      Filter: false,
      filter: "authorisationnumber",
      Cell: (rowInfo: any) => (
        <table>
          <tr>
            <td>
              <a
                href={GetLicenceVersionUrlFromRow(rowInfo.row)}
                target="_blank"
              >
                {rowInfo.value}
              </a>
            </td>
          </tr>
        </table>
      ),
      sortType: "basic",
    },
    {
      Header: "Name",
      accessor: "authorisationname",
      Filter: false,
      sortType: "basic",
    },
    {
      Header: "Authorisation Type",
      accessor: "authorisationtype",
      Filter: CreateStaticSelectColumnFilter(
        authorisationTypes.list.map((authType) => authType.value)
      ),
    },
    {
      Header: "Organisation",
      accessor: "organisation",
      Filter: false,
    },
    {
      Header: "Sector",
      accessor: "sector",
      Filter: CreateStaticSelectColumnFilter(
        sectorsList.list.map((sector) => sector.value)
      ),
    },
    {
      Header: "County",
      accessor: "county",
      Filter: CreateStaticSelectColumnFilter(
        countiesList.list.map((county) => county.value)
      ),
    },
    {
      Header: "Status",
      accessor: "status",
      Filter: CreateStaticSelectColumnFilter(
        statusesList.list.map((status) => status.value)
      ),
    },
    {
      Header: "Final Authorisation Issued",
      accessor: "finalauthorisationissueddate",
      Filter: false,
      sortType: "basic",
    },
  ];

  const tableColumnsBig: Column<Record<string, ILicenceSearchRow>>[] = [
    {
      Header: "Number",
      accessor: "authorisationnumber",
      Filter: false,
      filter: "authorisationnumber",
      Cell: (rowInfo: any) => (
        <table>
          <tr>
            <td>
              <a
                href={GetLicenceVersionUrlFromRow(rowInfo.row)}
                target="_blank"
              >
                {rowInfo.value}
              </a>
            </td>
          </tr>
        </table>
      ),
      sortType: "basic",
    },
    {
      Header: "Name",
      accessor: "authorisationname",
      Filter: false,
    },
    {
      Header: "Authorisation Type",
      accessor: "authorisationtype",
      Filter: CreateStaticSelectColumnFilter(
        authorisationTypes.list.map((authType) => authType.value)
      ),
      sortType: "basic",
    },
    {
      Header: "Organisation",
      accessor: "organisation",
      Filter: false,
    },
    {
      Header: "Sector",
      accessor: "sector",
      Filter: CreateStaticSelectColumnFilter(
        sectorsList.list.map((sector) => sector.value)
      ),
    },
    {
      Header: "County",
      accessor: "county",
      Filter: CreateStaticSelectColumnFilter(
        countiesList.list.map((county) => county.value)
      ),
    },
    {
      Header: "Status",
      accessor: "status",
      Filter: CreateStaticSelectColumnFilter(
        statusesList.list.map((status) => status.value)
      ),
    },
    {
      Header: "Open for Submissions?",
      accessor: "openforsubmissions",
      Filter: CreateStaticSelectColumnFilter(
        YesNoAllFilterValues.map((filterValue) => filterValue)
      ),
    },
    {
      Header: "Open for Objections?",
      accessor: "openforobjections",
      Filter: CreateStaticSelectColumnFilter(
        YesNoAllFilterValues.map((filterValue) => filterValue)
      ),
    },
    {
      Header: "Priority Application this Year?",
      accessor: "pdduethisyear",
      Filter: CreateStaticSelectColumnFilter(
        YesNoAllFilterValues.map((filterValue) => filterValue)
      ),
    },
    {
      Header: "Final Authorisation Issued",
      accessor: "finalauthorisationissueddate",
      Filter: false,
      sortType: "basic",
    },
  ];

  let fetchUrlFn = (
    pageNo: number,
    recordsPerPage: number,
    searchText: string,
    filters: any,
    orderBy?: EpaGridOrderByFetchParams
  ) => {
    return GetLicenceAdvancedSearchFetchUrl(
      pageNo,
      recordsPerPage,
      searchText,
      filters,
      orderBy
    );
  };

  let formatResponseFunction = (searchResponse: ILicenceSearchList) => {
    const array = searchResponse.list.map((result) => {
      return {
        licenceid: result.licenceid,
        authorisationnumber: result.authorisationnumber,
        authorisationname: result.authorisationname,
        authorisationtype: result.authorisationtype,
        organisation: result.organisation,
        finalauthorisationissueddate: formatDate(
          result.finalauthorisationissueddate
        ),
        county: result.county,
        status: result.status,
        openforsubmissions: result.openforsubmissions,
        openforobjections: result.openforobjections,
        pdduethisyear: result.pdduethisyear,
        sector: result.sector,
      } as ILicenceSearchRow;
    });

    return {
      list: array,
      length: searchResponse.length,
      page: searchResponse.page,
      count: searchResponse.count,
    };
  };

  const orderBy = {
    orderByAscending: true,
    orderByName: "Number",
    enabledOrderByColumns: ["Number", "Name", "Final Authorisation Issued"],
  };

  const results = (
    <div>
      {screenBreakpoints.isSmall && (
        <Grid
          columns={tableColumnsSmall}
          data={[]}
          fetchUrlFunction={fetchUrlFn}
          enablePagination={true}
          enableQuickFind={false}
          formatResponseFunction={formatResponseFunction}
          searchText={searchValue}
          orderBy={orderBy}
        />
      )}
      {screenBreakpoints.isMedium && (
        <Grid
          columns={tableColumnsMedium}
          data={[]}
          fetchUrlFunction={fetchUrlFn}
          enablePagination={true}
          enableQuickFind={false}
          formatResponseFunction={formatResponseFunction}
          searchText={searchValue}
          orderBy={orderBy}
        />
      )}
      {(screenBreakpoints.isLarge || screenBreakpoints.isExtraLarge) && (
        <Grid
          columns={tableColumnsBig}
          data={[]}
          fetchUrlFunction={fetchUrlFn}
          enablePagination={true}
          enableQuickFind={false}
          formatResponseFunction={formatResponseFunction}
          searchText={searchValue}
          orderBy={orderBy}
        />
      )}
    </div>
  );

  const searchContent = (
    <div>
      <LeapAlert />
      <LeapSearch searchChangedFn={searchChangedFn} />
      {results}
    </div>
  );

  return searchContent;
};

export default AdvancedSearch;
